import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from '../components/ui/button';

export const registerServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', async () => {
      try {
        const registration = await navigator.serviceWorker.register('/service-worker.js');
        console.log('ServiceWorker registered:', registration.scope);

        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                console.log('New version available! Refresh to update.');
              }
            });
          }
        });
      } catch (error) {
        console.error('ServiceWorker registration failed:', error);
      }
    });
  }
};

export const PWAInstallPrompt = () => {
  const { t } = useTranslation('components');
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                       window.navigator.standalone === true;
    setIsStandalone(standalone);

    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    if (standalone) return;

    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) return;
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    if (iOS && !standalone) {
      setTimeout(() => setShowPrompt(true), 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('PWA installed');
    }

    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', new Date().toISOString());
  };

  if (isStandalone || !showPrompt) return null;

  if (isIOS && showIOSInstructions) {
    return (
      <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 animate-in fade-in">
        <div className="bg-white rounded-t-3xl w-full max-w-lg p-6 pb-10 animate-in slide-in-from-bottom">
          <button
            onClick={() => setShowIOSInstructions(false)}
            className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600"
            aria-label={t('pwaClose')}
          >
            <X className="h-6 w-6" />
          </button>

          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-omu-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Smartphone className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">{t('pwaInstallTitle')}</h2>
            <p className="text-slate-500 mt-1">{t('pwaInstallSteps')}</p>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
              <div className="w-10 h-10 bg-omu-blue-500 text-white rounded-full flex items-center justify-center font-bold">
                1
              </div>
              <div className="flex-1">
                <p className="font-medium text-slate-900">{t('pwaStep1Title')}</p>
                <p className="text-sm text-slate-500">{t('pwaStep1Desc')}</p>
              </div>
              <div className="text-3xl">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-omu-blue-500">
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
                  <polyline points="16 6 12 2 8 6"/>
                  <line x1="12" y1="2" x2="12" y2="15"/>
                </svg>
              </div>
            </div>

            <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
              <div className="w-10 h-10 bg-omu-blue-500 text-white rounded-full flex items-center justify-center font-bold">
                2
              </div>
              <div className="flex-1">
                <p className="font-medium text-slate-900">{t('pwaStep2Title')}</p>
                <p className="text-sm text-slate-500">{t('pwaStep2Desc')}</p>
              </div>
              <div className="text-2xl">➕</div>
            </div>

            <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
              <div className="w-10 h-10 bg-omu-blue-500 text-white rounded-full flex items-center justify-center font-bold">
                3
              </div>
              <div className="flex-1">
                <p className="font-medium text-slate-900">{t('pwaStep3Title')}</p>
                <p className="text-sm text-slate-500">{t('pwaStep3Desc')}</p>
              </div>
              <div className="text-2xl">✓</div>
            </div>
          </div>

          <Button
            className="w-full mt-6 h-12 bg-omu-blue-500 hover:bg-omu-blue-600 text-base font-semibold rounded-xl"
            onClick={() => {
              setShowIOSInstructions(false);
              setShowPrompt(false);
              localStorage.setItem('pwa-prompt-dismissed', new Date().toISOString());
            }}
          >
            {t('pwaGotIt')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 animate-in slide-in-from-bottom-4 md:left-auto md:right-4 md:max-w-sm">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-4 relative">
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1 text-slate-400 hover:text-slate-600 transition-colors"
          aria-label={t('pwaClose')}
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-omu-blue-500 to-omu-blue-600 rounded-xl flex items-center justify-center shadow-lg">
            <Smartphone className="h-7 w-7 text-white" />
          </div>

          <div className="flex-1 min-w-0 pr-6">
            <h3 className="font-semibold text-slate-900 text-base">
              {t('pwaInstallTitle')}
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              {t('pwaPromptDesc')}
            </p>
          </div>
        </div>

        <div className="mt-4 flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleDismiss}
            className="flex-1"
          >
            {t('pwaLater')}
          </Button>
          {isIOS ? (
            <Button
              size="sm"
              onClick={() => setShowIOSInstructions(true)}
              className="flex-1 bg-omu-blue-500 hover:bg-omu-blue-600"
            >
              <Download className="h-4 w-4 mr-2" />
              {t('pwaInstall')}
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={handleInstall}
              className="flex-1 bg-omu-blue-500 hover:bg-omu-blue-600"
            >
              <Download className="h-4 w-4 mr-2" />
              {t('pwaInstall')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
